import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Badge, Tooltip, Text, Box } from "@chakra-ui/react";
import { DarwinAgentType } from "build-ts/common";
import { DarwinHeader, DarwinHeaderList } from "build-ts/common";
import { getAgentsList } from "services/EvaServices";
import Table, { TableColumn, TableFilter, TablePagination } from "components/common/Table";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import { useUser } from "context/UserContext";
import { getAgentTypeColorForDarwinEva } from "utils/helperFuncUtils";

interface DarwinAgentsSelectionProps {
  onSelectionChange?: (selectedItems: Set<string>, agentType?: string) => void;
  selectedItems?: Set<string>;
  hideActions?: boolean;
}

const DarwinAgentsSelection: React.FC<DarwinAgentsSelectionProps> = ({
  onSelectionChange,
  selectedItems = new Set(),
  hideActions = false,
}) => {
  const hasMounted = useRef(false);
  const { defaultTimeZone } = useUser();

  // State for data
  const [agents, setAgents] = useState<DarwinHeader[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [pagination, setPagination] = useState<TablePagination>({
    total: 0,
    offset: 0,
    has_more: false,
  });
  const [limit] = useState(50);

  // Agent filters
  const [filters, setFilters] = useState<Record<string, string>>({
    search: "",
    type: "all",
    active: "all",
  });
  const filtersRef = useRef(filters);
  const [sortBy, setSortBy] = useState("created_date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Keep ref in sync with state
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Fetch agents from Darwin API
  const fetchAgents = useCallback(
    async (
      reset = false,
      currentSortBy = "created_date",
      currentSortOrder = "asc",
      currentFilters: Record<string, string> = filters,
      newOffset?: number
    ) => {
      try {
        if (reset) {
          setLoading(true);
        }
        setError(null);

        const currentOffset = newOffset !== undefined ? newOffset : reset ? 0 : pagination.offset || 0;
        const params = new URLSearchParams({
          limit: limit.toString(),
          offset: currentOffset.toString(),
          sort_by: currentSortBy,
          sort_order: currentSortOrder,
        });

        if (currentFilters.type && currentFilters.type !== "all") {
          params.append("agent_type", currentFilters.type);
        }
        if (currentFilters.search && currentFilters.search.trim()) {
          params.append("name", currentFilters.search);
        }
        if (currentFilters.active && currentFilters.active !== "all") {
          params.append("is_active", currentFilters.active);
        }

        const result: DarwinHeaderList = DarwinHeaderList.fromJSON(await getAgentsList(params));

        if (result.headers) {
          setAgents(result.headers);

          if (result.total) {
            setPagination({
              total: result.total,
              has_more: currentOffset + limit < result.total,
              offset: currentOffset,
            });
          }
        } else {
          setAgents([]);
          setPagination({
            total: 0,
            has_more: false,
            offset: currentOffset,
          });
        }
      } catch (err) {
        const errorMsg = `Network error: ${err instanceof Error ? err.message : "Unknown error"}`;
        setError(errorMsg);
      } finally {
        setLoading(false);
      }
    },
    [limit, pagination.offset, filters]
  );

  // Load data on component mount
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      const loadData = async () => {
        await fetchAgents(true);
      };
      loadData();
    }
  }, [fetchAgents]);

  // Table event handlers
  const handlePageChange = useCallback(
    async (offset: number) => {
      await fetchAgents(false, sortBy, sortOrder, filters, offset);
    },
    [fetchAgents, sortBy, sortOrder, filters]
  );

  const handleSort = useCallback(
    async (newSortBy: string, newSortOrder: "asc" | "desc") => {
      setSortBy(newSortBy);
      setSortOrder(newSortOrder);
      await fetchAgents(true, newSortBy, newSortOrder, filters, 0);
    },
    [fetchAgents, filters]
  );

  const handleFilterChange = useCallback((newFilters: Record<string, string>) => {
    setFilters(newFilters);
    // Also update the ref immediately
    filtersRef.current = newFilters;
  }, []);

  const handleApplyFilters = useCallback(async () => {
    // Use the ref to get the latest filter values
    await fetchAgents(true, sortBy, sortOrder, filtersRef.current, 0);
  }, [fetchAgents, sortBy, sortOrder]);

  const handleClearFilters = useCallback(async () => {
    const clearedFilters = { search: "", type: "all", active: "all" };
    setFilters(clearedFilters);
    filtersRef.current = clearedFilters; // Update ref immediately
    setSortBy("created_date");
    setSortOrder("asc");
    await fetchAgents(true, "created_date", "asc", clearedFilters, 0);
  }, [fetchAgents]);

  const handleSelectionChange = useCallback(
    (newSelectedAgents: Set<string>) => {
      // Get the agent type if an agent is selected
      let agentType: string | undefined;
      if (newSelectedAgents.size > 0) {
        const selectedAgentId = newSelectedAgents.values().next().value;
        const selectedAgent = agents.find(agent => agent.id === selectedAgentId);
        agentType = selectedAgent?.type;
      }
      // Pass both selection and agent type in a single callback
      onSelectionChange?.(newSelectedAgents, agentType);
    },
    [onSelectionChange, agents]
  );

  // Get agent types from DarwinAgentType enum
  const agentTypes = useMemo(() => {
    return Object.values(DarwinAgentType).filter(
      value => typeof value === "string" && value !== "UNRECOGNIZED" && value !== "UNKNOWN_AGENT_TYPE"
    ) as string[];
  }, []);

  // Table configuration
  const tableColumns: TableColumn<DarwinHeader>[] = useMemo(
    () => [
      {
        key: "name",
        label: "Agent Name",
        sortable: true,
        className: "eva-name-column",
        render: agent => (
          <Tooltip label={agent.name} placement="top">
            <Text fontWeight="semibold" noOfLines={1} wordBreak="break-word" mb={0}>
              {agent.name}
            </Text>
          </Tooltip>
        ),
      },
      {
        key: "agent_type",
        label: "Type",
        className: "eva-type-column",
        render: agent => {
          const agentType = agent.type as keyof typeof DarwinAgentType;
          return (
            <Badge colorScheme={getAgentTypeColorForDarwinEva(DarwinAgentType[agentType])}>
              {agent.type || "Unknown"}
            </Badge>
          );
        },
      },
      {
        key: "is_active",
        label: "Status",
        className: "eva-status-column",
        render: agent => (
          <Badge colorScheme={agent.is_active ? "green" : "red"} variant="solid">
            {agent.is_active ? "Active" : "Inactive"}
          </Badge>
        ),
      },
      {
        key: "created_date",
        label: "Created",
        sortable: true,
        className: "eva-date-column",
        render: agent => (
          <Text fontSize="sm" color="gray.600" mb={0}>
            {epochConverterWithTimeZone(agent.created_date || 0, "dd-MMM-yyyy, hh:mm a", defaultTimeZone)}{" "}
            {defaultTimeZone.toUpperCase()}
          </Text>
        ),
      },
      {
        key: "last_modified_date",
        label: "Modified",
        sortable: true,
        className: "eva-date-column",
        render: agent => (
          <Text fontSize="sm" color="gray.600" mb={0}>
            {epochConverterWithTimeZone(agent.last_modified_date || 0, "dd-MMM-yyyy, hh:mm a", defaultTimeZone)}{" "}
            {defaultTimeZone.toUpperCase()}
          </Text>
        ),
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
    ],
    []
  );

  const tableFilters: TableFilter[] = useMemo(
    () => [
      {
        key: "search",
        type: "text",
        label: "Search",
        placeholder: "Search agents...",
        width: "300px",
      },
      {
        key: "type",
        type: "select",
        label: "Type",
        options: [{ value: "all", label: "All Types" }, ...agentTypes.map(type => ({ value: type, label: type }))],
        width: "200px",
      },
      {
        key: "active",
        type: "select",
        label: "Status",
        options: [
          { value: "all", label: "All Status" },
          { value: "true", label: "Active" },
          { value: "false", label: "Inactive" },
        ],
        width: "150px",
      },
    ],
    [agentTypes]
  );

  return (
    <Box height="100%" overflowY="hidden">
      <Table
        data={agents}
        columns={tableColumns}
        loading={loading}
        error={error}
        pagination={pagination}
        limit={limit}
        onPageChange={handlePageChange}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        filters={tableFilters}
        filterValues={filters}
        onFilterChange={handleFilterChange}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        selectable={true}
        selectedItems={selectedItems}
        onSelectionChange={handleSelectionChange}
        getItemId={agent => agent.id || ""}
        singleSelection={true}
        emptyMessage="No agents found"
        emptySubMessage="Try adjusting your search criteria"
      />
    </Box>
  );
};

export default DarwinAgentsSelection;
